import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
// Type-only import: erased at build time so this utility carries no runtime
// dependency on the (heavy) Icon/config module graph. The caller resolves the
// record-type default icon and passes the component in.
import type { IconLike } from "components/Icon/Icon";

// The static app-default favicon shipped in index.html. Used as the final
// fallback when the opened record has neither a user-defined icon nor a
// record-type default icon.
const DEFAULT_FAVICON_HREF = "/favicon.ico";

// Colour used to tint the record-type default (mdi-react SVG) icons when they
// are rasterised into a favicon. mdi icons otherwise render with `currentColor`,
// which is meaningless in a detached data-URI. Uses the Cloudomation brand blue
// (secondary-blue #0e86d1 — bright enough to stay legible on both light and dark
// browser tab strips). User-uploaded container icons are full-colour images and
// are used as-is (no tint).
const DEFAULT_ICON_COLOR = "#0e86d1";

// SVG namespace. renderToStaticMarkup of an mdi-react (or any JSX) <svg> does
// NOT emit an `xmlns` attribute — that is fine for an SVG inlined in the live
// DOM (the HTML parser assumes the SVG namespace inside <svg>), but a
// STANDALONE SVG loaded as an image (a `data:image/svg+xml,...` favicon href)
// is parsed as an XML document and MUST declare its namespace or it fails to
// parse -> the browser shows its built-in "no icon" fallback. This missing
// xmlns was the root cause of the per-record favicon regression.
const SVG_NS = "http://www.w3.org/2000/svg";

const isDataUri = (value: unknown): value is string =>
    typeof value === "string" && value.startsWith("data:");

// Normalise the serialised SVG markup so it is valid as a standalone favicon
// image: (1) ensure the SVG namespace is declared on the root element, and
// (2) ensure width/height are absolute pixel numbers. A favicon renders with
// no CSS/font context, so relative units (e.g. mdi's default `1em`, or any
// non-numeric value) collapse to zero -> a blank/invisible icon. Only the
// opening <svg ...> tag is touched; the path children are left untouched.
const normalizeSvgMarkup = (markup: string): string =>
    markup.replace(/<svg\b([^>]*)>/i, (_full, attrs: string): string => {
        let a = attrs;
        if (!/\bxmlns\s*=/.test(a)) {
            a = ` xmlns="${SVG_NS}"${a}`;
        }
        // Coerce any non-numeric width/height (e.g. "1em") to a fixed pixel size.
        a = a.replace(
            /\b(width|height)\s*=\s*"([^"]*)"/gi,
            (whole, name: string, val: string): string =>
                /^\d+(\.\d+)?$/.test(val) ? whole : `${name}="64"`
        );
        if (!/\bwidth\s*=/.test(a)) a += ' width="64"';
        if (!/\bheight\s*=/.test(a)) a += ' height="64"';
        return `<svg${a}>`;
    });

const getFaviconLinkElement = (): HTMLLinkElement => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    return link;
};

// Serialise an mdi-react / component icon to an inline SVG data-URI usable as a
// <link rel="icon"> href. Returns null if the component does not render an SVG
// (defensive — falls back to the app default).
const iconComponentToDataUri = (
    icon: IconLike,
    color: string = DEFAULT_ICON_COLOR
): string | null => {
    try {
        const markup = renderToStaticMarkup(
            // mdi-react components accept `color` and `size`; a plain
            // ComponentType<{className}> ignores them harmlessly.
            createElement(icon as React.ComponentType<{ color?: string; size?: number }>, {
                color,
                size: 64,
            })
        );
        if (!markup.includes("<svg")) {
            return null;
        }
        return `data:image/svg+xml,${encodeURIComponent(normalizeSvgMarkup(markup))}`;
    } catch {
        return null;
    }
};

// Resolve the favicon href for the currently-opened record, honouring the
// fallback chain: (1) a user-defined icon stored on the record itself
// (containers such as workspace/project/bundle store an uploaded image as a
// data-URI on `icon`), then (2) the record-type default icon (an mdi-react SVG
// component the caller resolved via getByRecordTypeName), then (3) the app
// default favicon.
// `curatedIcon` is the component the caller resolved from a `mdi:<key>` curated
// icon reference stored on the record (favicon.ts deliberately carries no
// runtime dependency on the Icon/IconCollection module graph, so the caller
// does the key→component lookup). A user-chosen curated glyph is rendered like
// the record-type default — a brand-tinted standalone SVG — but takes
// precedence over it, since it is an explicit user choice.
export const resolveRecordFaviconHref = (
    userIcon: unknown,
    typeDefaultIcon: IconLike | undefined,
    curatedIcon?: IconLike
): string => {
    if (isDataUri(userIcon)) {
        return userIcon;
    }
    const chosenIcon = curatedIcon ?? typeDefaultIcon;
    if (chosenIcon) {
        const dataUri = iconComponentToDataUri(chosenIcon);
        if (dataUri) {
            return dataUri;
        }
    }
    return DEFAULT_FAVICON_HREF;
};

const applyFaviconHref = (href: string): void => {
    const link = getFaviconLinkElement();
    if (link.getAttribute("href") === href) {
        return;
    }
    link.setAttribute("href", href);
    if (href.startsWith("data:image/svg")) {
        link.setAttribute("type", "image/svg+xml");
    } else if (href.startsWith("data:image/")) {
        const match = href.match(/^data:(image\/[a-zA-Z0-9.+-]+)/);
        link.setAttribute("type", match ? match[1] : "image/png");
    } else {
        link.removeAttribute("type");
    }
};

// Set the browser-tab favicon to the icon of the currently-opened record.
// `userIcon` is the record's own icon field (a data-URI for containers, else
// absent); `typeDefaultIcon` is the record-type default icon component.
export const applyRecordFavicon = (
    userIcon: unknown,
    typeDefaultIcon: IconLike | undefined,
    curatedIcon?: IconLike
): void => {
    applyFaviconHref(resolveRecordFaviconHref(userIcon, typeDefaultIcon, curatedIcon));
};

// Restore the app-default favicon (no single record open — list/search/trash).
export const resetFavicon = (): void => {
    applyFaviconHref(DEFAULT_FAVICON_HREF);
};
